import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useStateValue } from "../../StateProvider";
import { tablesDB, DB_ID, ORDERS_TABLE, CART_TABLE } from "../../appwrite";
import { Query } from "appwrite";
import Navbar from "../../Components/Navbar/Navbar";
import Footer from "../../Components/Footer/Footer";
import "./OrderDetails.css";
import LoadingScreen from "../../LoadingScreen";
import Invoice from "./Invoice";

const formatPrice = (price) => {
  if (isNaN(price)) return "Invalid Price";
  return parseFloat(price).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const capitalizeWords = (str) => {
  return str
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

const formatFulfillmentStatus = (status) => {
  if (!status) return "N/A";
  return capitalizeWords(status.replace(/-/g, " "));
};

function OrderDetails() {
  const [order, setOrder] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const { orderId } = useParams();
  const [{ user }] = useStateValue();
  const navigate = useNavigate();
  const [isInvoiceVisible, setInvoiceVisible] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
    } else {
      const fetchOrderDetails = async () => {
        try {
          const response = await tablesDB.getRow({
            databaseId: DB_ID,
            tableId: ORDERS_TABLE,
            rowId: orderId,
          });

          if (response) {
            const orderData = { ...response, orderId: response.$id || response.orderId };
            if (orderData.userId !== user.$id) {
              navigate("/orders");
              return;
            }
            setOrder(orderData);
            await fetchCartItems(orderData.orderId || response.$id);
          } else {
            navigate("/orders");
          }
        } catch {
          navigate("/orders");
        } finally {
          setLoading(false);
        }
      };
      fetchOrderDetails();
    }
  }, [user, navigate, orderId]);

  const fetchCartItems = async (orderId) => {
    try {
      const response = await tablesDB.listRows({
        databaseId: DB_ID,
        tableId: CART_TABLE,
        queries: [Query.equal("orders", orderId)],
      });
      if (response.rows || response.documents) {
        setCartItems(response.rows || response.documents);
      }
    } catch {}
  };

  useEffect(() => {
    document.body.style.overflow = isInvoiceVisible ? "hidden" : "auto";
  }, [isInvoiceVisible]);

  const generateInvoice = async () => {
    try {
      if (order.invoice) {
        setInvoiceNumber(order.invoice);
        setInvoiceVisible(true);
      } else {
        const randomDigits = Math.floor(13 + Math.random() * 90);
        const newInvoiceNumber = `10${randomDigits}`;
        await tablesDB.updateRow({
          databaseId: DB_ID,
          tableId: ORDERS_TABLE,
          rowId: orderId,
          data: { invoice: newInvoiceNumber },
        });
        setOrder((prev) => ({ ...prev, invoice: newInvoiceNumber }));
        setInvoiceNumber(newInvoiceNumber);
        setInvoiceVisible(true);
      }
    } catch {}
  };

  if (loading) return <LoadingScreen />;
  if (!order) return <div>Order not found</div>;

  const discountRate = 0.075;
  const discountAmount = order.orderTotal * discountRate;
  const subtotalWithDiscount = order.orderTotal - discountAmount;

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      if (typeof timestamp === "object" && timestamp.seconds)
        return new Date(timestamp.seconds * 1000).toLocaleDateString();
      return new Date(timestamp).toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  const parseInfoArrayJSON = (infoArray) => {
    if (!infoArray || !Array.isArray(infoArray)) return {};
    const result = {};
    infoArray.forEach((item) => {
      if (typeof item === "string") {
        try {
          Object.assign(result, JSON.parse(item));
        } catch {
          const colonIndex = item.indexOf(":");
          if (colonIndex > -1) {
            result[item.substring(0, colonIndex).trim()] = item.substring(colonIndex + 1).trim();
          }
        }
      }
    });
    return result;
  };

  const shippingInfo = parseInfoArrayJSON(order.shippingInfo);
  const billingInfo = parseInfoArrayJSON(order.billingInfo);

  const parsedCart = (order.cart || []).map((item) => {
    if (typeof item === "string") { try { return JSON.parse(item); } catch { return {}; } }
    return item;
  });
  const displayCartItems = cartItems.length > 0 ? cartItems : parsedCart;

  const status = formatFulfillmentStatus(order.fulfillmentStatus);
  const statusClass = status.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="od-page">
      <Navbar />

      <div className="od-wrap">

        {/* Page header */}
        <div className="od-header">
          <div>
            <h1 className="od-header__title">
              Order #{order.orderNumber || (order.orderId || "").slice(-8)}
            </h1>
            <div className="od-header__meta">
              <p className="od-header__date">{formatDate(order.$createdAt)}</p>
              <span className={`od-pay-badge od-pay-badge--${(order.paymentStatus || "pending").toLowerCase()}`}>
                {(order.paymentStatus || "Pending").charAt(0).toUpperCase() + (order.paymentStatus || "pending").slice(1)}
              </span>
            </div>
          </div>
          <span className={`od-status od-status--${statusClass}`}>{status}</span>
        </div>

        {/* Order items card */}
        <div className="od-card">
          <h2 className="od-card__title">Items</h2>
          {displayCartItems.length > 0 ? (
            displayCartItems.map((item, index) => (
              <div className="od-item" key={index}>
                <img
                  className="od-item__img"
                  src={item.image || item.productImage}
                  alt={item.title || item.productName}
                />
                <div className="od-item__info">
                  <p className="od-item__name">{item.title || item.productName}</p>
                  {(item.description || item.productDescription) && (
                    <p className="od-item__desc">{item.description || item.productDescription}</p>
                  )}
                </div>
                <div className="od-item__right">
                  <p className="od-item__price">₦{formatPrice(item.price)}</p>
                  <p className="od-item__qty">Qty: {item.quantity}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="od-empty">No items found in this order.</p>
          )}
        </div>

        {/* Dispatch Proof */}
        {order.dispatchProof && order.dispatchProof.length > 0 && (
          <div className="od-card od-dispatch-card">
            <h2 className="od-card__title">Dispatched</h2>
            <p className="od-dispatch-note">
              Your seller confirmed this order was dispatched. Photos below were taken at the time of handoff.
            </p>
            <div className="od-dispatch-grid">
              {order.dispatchProof.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`Dispatch photo ${i + 1}`}
                  className="od-dispatch-img"
                />
              ))}
            </div>
          </div>
        )}

        {/* Info grid */}
        <div className="od-grid">
          <div className="od-card">
            <h2 className="od-card__title">Shipping Address</h2>
            <p className="od-card__row">{shippingInfo.address || "N/A"}</p>
            <p className="od-card__row">
              {[shippingInfo.city, shippingInfo.state, shippingInfo.country].filter(Boolean).join(", ") || "N/A"}
            </p>
            <p className="od-card__row">{shippingInfo.phoneNumber || "N/A"}</p>
          </div>

          <div className="od-card">
            <h2 className="od-card__title">Contact</h2>
            <p className="od-card__row">{shippingInfo.email || billingInfo.email || "N/A"}</p>
            <p className="od-card__row">{shippingInfo.phoneNumber || billingInfo.phoneNumber || "N/A"}</p>
          </div>

          {(order.riderName || order.riderPhone) && (
            <div className="od-card">
              <h2 className="od-card__title">Rider Info</h2>
              {order.riderName && <p className="od-card__row"><strong>Name:</strong> {order.riderName}</p>}
              {order.riderPhone && <p className="od-card__row"><strong>Phone:</strong> {order.riderPhone}</p>}
            </div>
          )}

          <div className="od-card">
            <h2 className="od-card__title">Order Summary</h2>
            <div className="od-summary-row">
              <span>Subtotal (w/discount)</span>
              <span>₦{formatPrice(subtotalWithDiscount)}</span>
            </div>
            <div className="od-summary-row">
              <span>Tax</span>
              <span>7.5%</span>
            </div>
            <div className="od-summary-row od-summary-row--total">
              <span>Total</span>
              <strong>₦{formatPrice(order.orderTotal)}</strong>
            </div>
          </div>
        </div>

        {/* Invoice + links */}
        <div className="od-footer">
          <button className="od-invoice-btn" onClick={generateInvoice}>
            Download Invoice
          </button>
          <div className="od-links">
            <span className="od-link" onClick={() => navigate("/frequently-asked-questions")}>F.A.Q</span>
            <span className="od-link" onClick={() => navigate("/refund-policy")}>Refund Policy</span>
            <span className="od-link" onClick={() => navigate("/privacy-policy")}>Privacy Policy</span>
            <span className="od-link" onClick={() => navigate("/terms-and-conditions")}>Terms & Conditions</span>
          </div>
        </div>

      </div>

      {isInvoiceVisible && (
        <div className="contact-overlay">
          <div className="contact-form-container" onClick={(e) => e.stopPropagation()}>
            <Invoice
              isVisible={isInvoiceVisible}
              onClose={() => setInvoiceVisible(false)}
              order={order}
              cartItems={displayCartItems}
              invoiceNumber={invoiceNumber}
              shippingInfo={shippingInfo}
              billingInfo={billingInfo}
            />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default OrderDetails;
